/**
 * Helper method to clean table filters for state storage.
 *
 * Object keys with a value of undefined get dropped, so here we replace those values with null
 * else previous values would be kept instead of the new null value.
 *
 * @param filters - The filter map
 * @param cleanEmptyStrings - If true, empty strings will be stripped out of the filters object
 * @param stripEmptyValues - If true, empty arrays, empty strings, null and undefined values will be stripped out of the filters object
 * @param pruneMap - A Map of properties and values that should be pruned if present in the filters
 * @param nullableCriteria - An array of filter keys that are permitted to contain a null value
 */
export function cleanFilters<T>(filters: T,
    cleanEmptyStrings: boolean = false,
    stripEmptyValues: boolean = false,
    pruneMap: Record<string, any[]> = {},
    nullableCriteria: Array<keyof T> = []): T {

    if (!filters) {
        return {} as T;
    }

    const filtersAsAny: any = filters;

    Object.keys(filters)
        .forEach((key: string) => {
            if (filtersAsAny[key] === undefined) {
                filtersAsAny[key] = null;
            } else if (key in pruneMap) {
                // If the filter parameter is prunable, and the current value is a prunable value, then nullify it
                if (pruneMap[key].includes(filtersAsAny[key])) {
                    // remove value if filter is nullable
                    if (nullableCriteria.includes(key as keyof T)) {
                        delete filtersAsAny[key];
                    } else {
                        filtersAsAny[key] = null;
                    }
                }
            } else if (typeof filtersAsAny[key] === 'string') {
                if (filtersAsAny[key].trim() === '' && cleanEmptyStrings) {
                    filtersAsAny[key] = null;
                }
            }

            // lodash merge() will merge the previous array with a new one, rather than replacing it with an empty array
            if (Array.isArray(filtersAsAny[key]) && filtersAsAny[key].length === 0) {
                filtersAsAny[key] = null;
            }
        });

    if (stripEmptyValues) {
        Object.keys(filtersAsAny)
            .reverse()
            .forEach((key: string) => {
                // Keep values that are permitted to be null
                if (nullableCriteria.includes(key as keyof T)) {
                    return;
                }

                const val: any = filtersAsAny[key];

                const isEmptyArray: boolean = Array.isArray(val) && val.length === 0;
                const isEmpty: boolean = [null, undefined, ''].includes(val);

                if (isEmptyArray || isEmpty) {
                    delete filtersAsAny[key];
                }
            });
    }

    return filters;
}

/**
 *
 *
 * @param obj - An object to remove keys from that are not found within T
 * @param keys - []
 */
export function filterObjectByKeys<T, K extends keyof T>(obj: T, keys: K[]): void {
    if (!obj) {
        return;
    }

    Object.keys(obj)
        .reverse()
        .forEach((key: string) => {
            if (!keys.includes(key as any)) {
                delete (<any>obj)[key];
            }
        });
}

/**
 *
 * ## Usage
 * ### Standard Example
 *
 * ```
 *   const filters:any = {
 *      name: 'fred',
 *      parents: 'alfred,margret',
 *      age: 10
 *   }
 *
 *   const filtersMutated = applyFilterWildCardCriteria(filters, ['name']);
 *
 *   returns {
 *      name: '%fred%',
 *      parents: 'alfred,margret',
 *      age: 10
 *   }
 *
 *   const filtersMutated = applyFilterWildCardCriteria(filters, ['name'], ['parents']);
 *
 *   returns {
 *      name: '%fred%',
 *      parents: '%alfred%,%margret%',
 *      age: 10
 *   }
 *
 *   const filtersMutated = applyFilterWildCardCriteria(filters, ['name'], ['parents'], "->");
 *
 *   returns {
 *      name: '%fred%',
 *      parents: '%alfred%->%margret%',
 *      age: 10
 *   }
 * ```
 *
 * @param filters
 * @param wildcardCriteria
 * @param delimitedWildCardCriteria
 * @param delimiter
 */
export function applyFilterWildCardCriteria<T>(filters: T,
    wildcardCriteria: string[],
    delimitedWildCardCriteria: string[] = [],
    delimiter: string = ','): T {

    if (!filters) {
        return {} as T;
    }

    const filtersAsAny: any = filters;

    Object.keys(filtersAsAny)
        .filter((key: string) => wildcardCriteria.includes(key))
        .forEach((key: string) => {
            const value: any = filtersAsAny[key];

            if (Array.isArray(value)) {
                filtersAsAny[key] = value.map((valueItem: string) => addWildCard(valueItem));
            } else {
                filtersAsAny[key] = addWildCard(value)
            }
        });

    Object.keys(filtersAsAny)
        .filter((key: string) => delimitedWildCardCriteria.includes(key))
        .forEach((key: string) => {
            const value: any = filtersAsAny[key];

            filtersAsAny[key] = addWildCardToDelimitedValues(value, delimiter);
        });

    return filtersAsAny;
}

/**
 * Add wildcard character '%' to the start and end of the given value
 *
 * ## Usage
 * ### Standard Example
 *
 * ```
 *   addWildCard('bob'); // returns '%bob%'
 * ```
 */
export function addWildCard(value: string): string {
    if (!value) {
        return value;
    }

    if (!value.startsWith('%')) {
        value = '%' + value;
    }
    if (!value.endsWith('%')) {
        value = value + '%';
    }

    return value;
}

/**
 * Add wildcard character '%' to the start and end of each delimited value
 * delimiter defaults to ',' but can be overridden
 *
 * ## Usage
 * ### Standard Example
 *
 * ```
 *   addWildCardToDelimitedValues('bob,mike,fred'); // returns '%bob%,%mike%,%fred%'
 * ```
 */
export function addWildCardToDelimitedValues(value: string, delimiter: string = ','): string {
    if (!value) {
        return value;
    }

    return value.split(delimiter)
        .filter(Boolean)
        .map(addWildCard)
        .join(delimiter);
}